import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { OrderMarketTypeEnum } from '@toro/common/shared/enum/orders/market-type.enum';
import { OrderSideEnum } from '@toro/common/shared/enum/orders/side.enum';
import { SegmentMarketTypeEnum } from '@toro/common/shared/enum/segment/segment-market-type.enum';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { DialogPanelComponentContent } from '@toro/common/shared/models/dialogs/DialogPanelComponentContent';
import { StockOrderType } from '@toro/common/shared/models/stocks-exchange/stock-order.model';
import { StrategyRequestLegPriceType } from '@toro/common/shared/models/stocks-exchange/strategy-request-leg-price-type.enum';
import { StrategyRequestLegValidityType } from '@toro/common/shared/models/stocks-exchange/strategy-request-leg-validity-type.enum';
import { StrategyRequestOrderType } from '@toro/common/shared/models/stocks-exchange/strategy-request-order-type.enum';
import { AuthorizationWraperService } from '@toro/common/shared/services/authorization-wraper/authorization.wraper.service';
import { DialogsService } from '@toro/common/shared/services/dialogs.service';
import { ApplicationMessagesAction } from '@toro/common/shared/state-management/application-messages/application-message.action';
import { StocksMarketAction } from '@toro/common/shared/state-management/stocks-market/stocks-market.action';
import { TrackingsService } from 'apps/stock/src/app/services/tracking.service';
import { Subscription } from 'rxjs/Subscription';
import { StocksExchangeCheckoutAction } from '../../../../state-management/stocks-exchange-checkout/stocks-exchange-checkout.action';
import { StocksExchangeCheckoutEffects } from '../../../../state-management/stocks-exchange-checkout/stocks-exchange-checkout.effects';
import { StocksMarketInteractionsService } from '../../services/stocks-market-interactions.service';
import { DialogSuccessBuyComponent } from '../dialog-success-buy/dialog-success-buy.component';
import { DialogSuccessSellComponent } from '../dialog-success-sell/dialog-success-sell.component';
import { DialogBuyAndSellConfirmation } from './dialog-buy-and-sell-confirmation.model';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'dialog-buy-and-sell-confirmation',
    templateUrl: './dialog-buy-and-sell-confirmation.component.html',
    styleUrls: ['../common/dialog-right.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
})
export class DialogBuyAndSellConfirmationComponent extends DialogPanelComponentContent implements OnInit, OnDestroy {
    public form: FormGroup;
    public quoteValue: number;
    public busy: boolean = false;
    public searchTypeOptions: any[] = [
        {item: 'melhor preço disponível', value: 0},
        {item: 'preço personalizado', value: 1},
    ];

    public get isFuture(): boolean {
        return this.data.marketType === OrderMarketTypeEnum.Future;
    }

    public get isFractionated() {
        return !Number.isInteger(this.data.amount / this.data.standardLot);
    }

    public get investmentStep(): number {
        if (this.isFractionated) {
            return 1;
        } else {
            return this.data.standardLot;
        }
    }

    private investmentType: any;
    private disposableSubscriptions = new Array<Subscription>();

    constructor(
        public element: ElementRef,
        private dialogReference: MatDialogRef<DialogBuyAndSellConfirmationComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogBuyAndSellConfirmation,
        private formBuilder: FormBuilder,
        private dialogsService: DialogsService,
        private store: Store<AppState>,
        private authorizationWraper: AuthorizationWraperService,
        private stocksExchangeCheckoutEffects: StocksExchangeCheckoutEffects,
        private interactions: StocksMarketInteractionsService,
        private tracking: TrackingsService
    ) {
        super(element, true);

        this.form = formBuilder.group({
            stocksCount: [ this.data.amount, Validators.required],
            customPrice: 0,
            searchType: 0,
        });
    }

    public ngOnInit() {
        super.ngOnInit();

        const customPriceControl = this.form.get('customPrice');
        const searchTypeControls = this.form.get('searchType');
        // searchTypeControls.setValue(0);

        // adiciona/remove validação do campo personalizado quando o tipo de preço é alterado
        searchTypeControls.valueChanges.subscribe((value) => {
            if (value === 1) {
                if (customPriceControl.value === 0) {
                    customPriceControl.setValue(this.quoteValue);
                }
                customPriceControl.setValidators([Validators.required]);
            } else {
                customPriceControl.setValidators(null);
            }
            this.form.updateValueAndValidity();
        });

        this.disposableSubscriptions.push(
            this.store.select((state) => state.stocksExchange.stocksMarket).pipe(
                filter((state) => state.quote[this.data.symbol])
            ).subscribe((state) => {
                const currentQuotation = state.quote[this.data.symbol];
                this.quoteValue =
                    currentQuotation.PriceLastTrade || currentQuotation.OpeningPrice || currentQuotation.ClosingPricePreviousDay;
            }),
        );

        this.disposableSubscriptions.push(
            this.stocksExchangeCheckoutEffects.newOrder$
                .subscribe((action) => {
                    if (action.type.equals(StocksExchangeCheckoutAction.newOrderSuccess)) {
                        this.confirmSuccessAction();
                    }
                    if (action.type.equals(StocksExchangeCheckoutAction.newOrderError)) {
                        this.store.dispatch({ type: ApplicationMessagesAction.genericError, payload: action.payload });
                        this.busy = false;
                    }
                }),
        );

        this.store.dispatch({ type: StocksMarketAction.getQuote, payload:  this.data.symbol});
    }

    /** Finaliza o componente */
    public dispose() {
        super.close(this.dialogReference, null);
    }

    public ngOnDestroy() {
        this.disposableSubscriptions.forEach((subscription) => {
            if (subscription) subscription.unsubscribe();
        });
    }

    public onSubmit(event: Event) {
        event.preventDefault();
        if (this.busy) return;
        this.busy = true;
        const order = this.composeOrderOrder();

    if (!this.data.buy) {
      this.tracking.sendMetricsToMixpanel('Order Sent', {
        'Product Area Origin': 'Meus investimentos',
        'Day Trade': 'Não se aplica',
        'Configuration Type': 'Não se aplica',
        'Market Type': this.isFuture
          ? SegmentMarketTypeEnum.BMF
          : SegmentMarketTypeEnum.BOVESPA,
        'Order Type': 'Close position',
        Quantity: order.orderQty,
        Direction: 'Venda',
        Ticker: order.symbol,
        Price: order.price,
        'Time Responsável': 'Roebling',
        specificProperties: {
          // this.form.controls.searchType.value == 0 é o melhor preço disponível
          // this.form.controls.searchType.value == 1 é o preço personalizado
          'Price Mode':
            this.form.controls.searchType.value == 0
              ? 'A mercado'
              : 'Personalizado'
        }
      });
    }

    this.store.dispatch({
      type: StocksExchangeCheckoutAction.newOrder,
      payload: { data: order }
    });
    }

    /** Cria uma ordem baseada nos dados fornecidos pelo cliente */
    public composeOrderOrder() {

        // tslint:disable-next-line: prefer-const
        let order = new StockOrderType();

        order.symbol = this.isFractionated ? this.data.symbol + 'F' : this.data.symbol;
        order.orderQty = this.form.get('stocksCount').value;
        order.isDaytrade = this.data.daytrade;

        // Preço personalizado
        if (this.form.controls.searchType.value === 1) {
            order.price = this.form.controls.customPrice.value;
            order.priceType = StrategyRequestLegPriceType.Limited;
        } else {
            // a mercado
            order.priceType = StrategyRequestLegPriceType.Market;
        }
        // venda ou compra
        order.side = (this.data.buy) ? OrderSideEnum.Compra : OrderSideEnum.Venda;
        // day trade
        order.validityType = StrategyRequestLegValidityType.Day;
        order.internalOrderType =  StrategyRequestOrderType.Input;

        return order;
    }

    /**
     * Abre o overlay de sucesso, se de ativo for vendido
     */
    private confirmSuccessAction() {
        this.dispose();
        if (this.data.marketOpen) {
            this.dialogsService.showComponnent((this.data.buy) ? DialogSuccessBuyComponent : DialogSuccessSellComponent);
        } else {
            this.interactions.showOrderBookedReceipt(this.data.marketOpeningTime , this.data.buy );
        }
    }
}
